import React, { type FC, useCallback, useLayoutEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import i18n from '@yandex-int/i18n';
import { Footer } from 'client/blocks-redesign/Footer/Footer';
import { HeaderQuickApps } from 'client/blocks-redesign/Header/Header';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { QuickAppsList } from 'client/blocks-redesign/quickApps/QuickAppsList/QuickAppsList';
import { routes } from 'client/lib/routes';
import { getLocalStorageSafe, setLocalStorageSafe } from 'client/lib/storage';
import { IQuickApp, TQuickAppsMeta } from 'client/model/privateApps';
import { Channel } from 'client/model/skill';
import * as keyset from 'i18n/PrivateApps';

import { QuickAppAgreementText } from '../labels/QuickAppAgreementText';
import { QuickAppSmartScreenLabel, QuickAppTvLabel } from '../labels/QuickAppsLabels';
import { QuickAppsAvailableSpace } from '../QuickAppsAvailableSpace/QuickAppsAvailableSpace';
import { QuickAppsModal } from '../QuickAppsModal/QuickAppsModal';
import { QuickAppsTutorial } from '../QuickAppsUserTutorial/QuickAppsTutorial';
import { QuickAppUploader } from '../QuickAppUploader/QuickAppUploader';

import styles from './QuickAppsPage.module.scss';

interface Props {
    quickApps?: Partial<IQuickApp>[];
    meta: TQuickAppsMeta | undefined;
    onAdd: (channel: Channel) => void;
    onDelete: (deletedId: string, channel: Channel) => Promise<void>;
}

const t = i18n(keyset);

export const QuickAppsPage: FC<Props> = ({ quickApps, meta, onAdd, onDelete }) => {
    const [conditionsModalIsOpen, setConditionsModalIsOpen] = useState(false);

    const history = useHistory();

    const lsConditionsKey = `quick_apps_accepted__${__appConfig.user.blackbox.uid}`;

    useLayoutEffect(() => {
        if (!getLocalStorageSafe(lsConditionsKey)) {
            setConditionsModalIsOpen(true);
        }
    }, [lsConditionsKey]);

    const onConditionsReject = useCallback(() => {
        history.push(routes.app.quickApps('start'));
    }, [history]);

    const onConditionsAccept = useCallback(() => {
        setLocalStorageSafe(lsConditionsKey, 'accepted');
        setConditionsModalIsOpen(false);
    }, [lsConditionsKey]);

    return (
        <>
            <div className={styles.quickAppsPage__layoutContainer}>
                <HeaderQuickApps />
                <section className={styles.quickAppsPage__container}>
                    <header className={styles.quickAppsPage__header}>
                        <h1 className={styles.quickAppsPage__title}>{t('my-apps')}</h1>
                        <QuickAppTvLabel classNameExternal={styles.quickAppsPage__headerLabel} />
                        <QuickAppSmartScreenLabel classNameExternal={styles.quickAppsPage__headerLabel} />
                    </header>
                    <QuickAppsAvailableSpace meta={meta} />
                    <QuickAppUploader onAdd={onAdd} meta={meta} />
                    <div className={styles.quickAppsPage__appCountTitle}>
                        {t('uploaded-apps-count')} {quickApps ? `(${quickApps.length})` : null}
                    </div>
                    <div className={styles.quickAppsPage__appCountDescription}>{t('find-in-my-tab')}</div>
                    <div>
                        {quickApps ? <QuickAppsList quickApps={quickApps} onDelete={onDelete} /> : <PageLoader />}
                    </div>
                    <QuickAppsTutorial />
                </section>
                <Footer />
            </div>
            <QuickAppsModal
                open={conditionsModalIsOpen}
                onAccept={onConditionsAccept}
                onClose={onConditionsReject}
                title={t('terms-of-service')}
                text={
                    // <>
                    //     {__appConfig.app.showQuickAppsPageToAllUsers ? null : (
                    //         <Label className={styles.quickAppsPage__ndaLabel} size="m" theme="info">
                    //             {'NDA'}
                    //         </Label>
                    //     )}
                    //     {wrapInQuickAppsAgreementsLink(t('accept-terms-notice'))}
                    // </>
                    <QuickAppAgreementText />
                }
            />
        </>
    );
};
